/**
 * 📌 Script: index.js
 * 📂 Caminho: src/router/index.js
 * 📝 Descrição: Define rotas (createRouter / createWebHistory),
 *    registra views e aplica guarda global baseada em sessão (UID) e
 *    permissões (Pinia). URLs limpas e prefixo interno /app.
 *
 * 🔄 Relação com o Projeto:
 *  - Navegação entre Vitrine (pública) e módulos internos SPS.
 *  - Usado por App.vue (<router-view />) e toda a UI.
 *  - Integra com useModulesStore para autorização por módulo.
 *
 * 📥 Entradas:
 *  - Sem parâmetros externos. Usa meta nas rotas e sessionStorage.
 *
 * 📤 Saídas:
 *  - Export default: instância do router (createRouter).
 *
 * 🔗 Dependências:
 *  - **vue-router:** createRouter, createWebHistory
 *  - **stores:** useModulesStore (permissões)
 *  - **views:** AnmeldungView, VitrineView, StartseiteView, etc.
 *
 * ⚠️ Segurança / Observações:
 *  - Frontend só indica; regras reais no backend (Firestore/Functions).
 *  - Rotas públicas: meta.public = true
 *  - Só login: meta.requiresLoginOnly = true
 *  - Login + permissão: meta.requiresAuth = true
 */

import { createRouter, createWebHistory } from "vue-router";
import { useModulesStore } from "@/stores/modulesStore";
import { useAppFlavorStore } from "@/stores/appFlavor";
import { toDbFormat } from "@/services/MatrikelService";
import AnmeldungView from "../views/AnmeldungView.vue";
import VitrineView from "../views/VitrineView.vue";
import ZeiterfassungView from "../views/ZeiterfassungView.vue";

const SpendenFormularView = () =>
  import(
    /* webpackChunkName: "route-spenden-formular" */
    "../views/SpendenFormularView.vue"
  );

const OutdoorView = () =>
  import(
    /* webpackChunkName: "route-outdoor" */
    "@/views/OutdoorView.vue"
  );

const AndernPasswortView = () =>
  import(
    /* webpackChunkName: "route-passwort-aendern" */
    "@/views/AndernPasswortView.vue"
  );

const StartseiteView = () =>
  import(
    /* webpackChunkName: "route-startseite" */
    "../views/StartseiteView.vue"
  );

const LeitungsbereichView = () =>
  import(
    /* webpackChunkName: "route-leitungsbereich" */
    "../views/LeitungsbereichView.vue"
  );

const KasseView = () =>
  import(
    /* webpackChunkName: "route-kasse" */
    "../views/KasseView.vue"
  );

const LieferungenView = () =>
  import(
    /* webpackChunkName: "route-lieferungen" */
    "../views/LieferungenView.vue"
  );

const MitarbeiterbereichView = () =>
  import(
    /* webpackChunkName: "route-mitarbeiterbereich" */
    "../views/MitarbeiterbereichView.vue"
  );

const SpendenannahmeView = () =>
  import(
    /* webpackChunkName: "route-spendenannahme" */
    "../views/SpendenannahmeView.vue"
  );

const VerkaufView = () =>
  import(
    /* webpackChunkName: "route-verkauf" */
    "../views/VerkaufView.vue"
  );

const VerwaltungView = () =>
  import(
    /* webpackChunkName: "route-verwaltung" */
    "../views/VerwaltungView.vue"
  );

const WerkstattView = () =>
  import(
    /* webpackChunkName: "route-werkstatt" */
    "../views/WerkstattView.vue"
  );

const WebsiteView = () =>
  import(
    /* webpackChunkName: "route-website" */
    "../views/WebsiteView.vue"
  );

const ZentraleView = () =>
  import(
    /* webpackChunkName: "route-zentrale" */
    "../views/ZentraleView.vue"
  );

const FahrerbereichView = () =>
  import(
    /* webpackChunkName: "route-fahrerbereich" */
    "../views/FahrerbereichView.vue"
  );

const MezzomoView = () =>
  import(
    /* webpackChunkName: "route-mezzomo" */
    "../views/MezzomoView.vue"
  );

const BenutzerhandbuchView = () =>
  import(
    /* webpackChunkName: "route-benutzerhandbuch" */
    "../views/BenutzerhandbuchView.vue"
  );

// ⚠️ Para títulos, carrega JSONs "core" dinamicamente.
const ctxCore = require.context("@/locales", false, /^\.\/[a-z]{2}\.json$/);
const ctxVitrine = require.context(
  "@/locales",
  false,
  /^\.\/vitrine-[a-z]{2}\.json$/
);

const MESSAGES = ctxCore.keys().reduce((acc, p) => {
  const m = p.match(/\.\/([a-z]{2})\.json$/i);
  const code = m ? m[1].toLowerCase() : null;
  if (!code) return acc;
  const mod = ctxCore(p);
  acc[code] = mod?.default || mod;
  return acc;
}, {});

// garante fallback
if (!MESSAGES.de) MESSAGES.de = {};

const VITRINE_MESSAGES = ctxVitrine.keys().reduce((acc, p) => {
  const m = p.match(/\.\/vitrine-([a-z]{2})\.json$/i);
  const code = m ? m[1].toLowerCase() : null;
  if (!code) return acc;
  const mod = ctxVitrine(p);
  acc[code] = mod?.default || mod;
  return acc;
}, {});

if (!VITRINE_MESSAGES.de) VITRINE_MESSAGES.de = {};

function tLocal(key) {
  let lang = "de";
  try {
    lang = localStorage.getItem("sps.lang") || "de";
  } catch (_) {
    lang = "de";
  }
  const dict = MESSAGES[lang] || MESSAGES.de || {};
  return key.split(".").reduce((acc, k) => acc?.[k], dict) || key;
}

function tVitrineLocal(key) {
  let lang = "de";
  try {
    lang = localStorage.getItem("vitrine.lang") || "de";
  } catch (_) {
    lang = "de";
  }
  const dict = VITRINE_MESSAGES[lang] || VITRINE_MESSAGES.de || {};
  const value = key.split(".").reduce((acc, k) => acc?.[k], dict);
  if (value) return value;
  return (
    key.split(".").reduce((acc, k) => acc?.[k], VITRINE_MESSAGES.de) || key
  );
}

const routes = [
  // Pública: homepage (Vitrine) em "/"
  // MODIFICADO
  {
    path: "/",
    name: "vitrine",
    component: VitrineView,
    meta: {
      public: true,
      titleFixed: "Praxis Secondhand",
    },
  },

  // Rota pública para Outdoor TV
  {
    path: "/outdoor",
    name: "outdoor",
    component: OutdoorView,
    meta: {
      public: true,
      titleFixed: "Outdoor | Praxis Secondhand",
    },
  },

  // Rota publica para solicitacao de Abholspenden.
  {
    path: "/spenden",
    name: "spendenFormular",
    component: SpendenFormularView,
    meta: {
      public: true,
      vitrineTitleI18nKey: "vitrine.spenden.pageTitle",
    },
  },

  // Login público (mantém URL nova e antiga)
  {
    path: "/app/login",
    name: "login",
    component: AnmeldungView,
    meta: {
      public: true,
      titleFixed: "🔒 Login | System Praxis Secondhand",
    },
  },
  {
    path: "/login",
    redirect: { name: "login" },
  },

  // Zeiterfassung (Público)
  {
    path: "/app/zeiterfassung",
    name: "zeiterfassung",
    component: ZeiterfassungView,
    meta: {
      public: true,
      titleFixed: "Zeiterfassung | Praxis Secondhand",
    },
  },

  // Reset/alterar senha (público)
  {
    path: "/passwort-aendern",
    name: "passwortAendern",
    component: AndernPasswortView,
    meta: { public: true },
  },

  // Interno: dashboard (somente logado, sem checar permissão)
  {
    path: "/app",
    name: "startseite",
    component: StartseiteView,
    meta: {
      requiresLoginOnly: true,
      titleFixed: "System Praxis Secondhand",
    },
  },

  // Interno: módulos (login + permissão) — i18n module.*
  {
    path: "/app/leitungsbereich",
    name: "flohmarkt",
    component: LeitungsbereichView,
    meta: { requiresAuth: true, moduleI18nKey: "module.leitungsbereich" },
  },
  {
    path: "/app/kasse",
    name: "kasse",
    component: KasseView,
    meta: { requiresAuth: true, moduleI18nKey: "module.kasse" },
  },

  {
    path: "/app/lieferungen",
    name: "lieferungen",
    component: LieferungenView,
    meta: { requiresAuth: true, moduleI18nKey: "module.lieferungen" },
  },
  {
    path: "/app/mitarbeiterbereich",
    name: "mitarbeiterbereich",
    component: MitarbeiterbereichView,
    meta: {
      requiresLoginOnly: true,
      moduleI18nKey: "module.mitarbeiterbereich",
    },
  },
  {
    path: "/app/spendenannahme",
    name: "spendenannahme",
    component: SpendenannahmeView,
    meta: { requiresAuth: true, moduleI18nKey: "module.spendenannahme" },
  },
  {
    path: "/app/verkauf",
    name: "verkauf",
    component: VerkaufView,
    meta: { requiresAuth: true, moduleI18nKey: "module.verkauf" },
  },
  {
    path: "/app/verwaltung",
    name: "verwaltung",
    component: VerwaltungView,
    meta: { requiresAuth: true, moduleI18nKey: "module.verwaltung" },
  },
  {
    path: "/app/werkstatt",
    name: "werkstatt",
    component: WerkstattView,
    meta: { requiresAuth: true, moduleI18nKey: "module.werkstatt" },
  },
  {
    path: "/app/webseite",
    name: "website_praxisgmbh",
    component: WebsiteView,
    meta: { requiresAuth: true, moduleI18nKey: "module.website_praxisgmbh" },
  },
  {
    path: "/app/zentrale",
    name: "zentrale",
    component: ZentraleView,
    meta: { requiresAuth: true, moduleI18nKey: "module.zentrale" },
  },
  {
    path: "/app/fahrerbereich",
    name: "fahrerbereich",
    component: FahrerbereichView,
    meta: { requiresAuth: true, moduleI18nKey: "module.fahrerbereich" },
  },

  // Interno: Mezzomo (Super Admin)
  {
    path: "/app/mezzomo",
    name: "mezzomo",
    component: MezzomoView,
    meta: {
      requiresAuth: true,
      requiresSupreme: true,
      moduleI18nKey: "module.mezzomo",
    },
  },

  // Interno: manual (somente logado, sem permissão)
  {
    path: "/app/benutzerhandbuch",
    name: "benutzerhandbuch",
    component: BenutzerhandbuchView,
    meta: {
      requiresLoginOnly: true,
      moduleI18nKey: "manualView.pageTitle",
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Guarda global: público → libera; loginOnly → exige UID;
// requiresAuth → exige UID e permissão (modulesStore.permissions[name])
router.beforeEach(async (to, from, next) => {
  const modulesStore = useModulesStore();

  const appFlavorStore = useAppFlavorStore();
  await appFlavorStore.initFlavor();

  if (to.path === "/") {
    if (appFlavorStore.flavor === "sps") {
      return next({ name: "login" });
    } else if (appFlavorStore.flavor === "zeiterfassung") {
      return next({ name: "zeiterfassung" });
    }
  }

  const uid = sessionStorage.getItem("uid");

  // título dinâmico antes da navegação
  setTitle(to);

  // SEO dinâmico: robots e canonical
  setSeoMeta(to);

  // Público
  if (to.meta && to.meta.public) return next();

  // Só logado
  if (to.meta && to.meta.requiresLoginOnly) {
    if (uid) return next();
    return next({ name: "login", query: { return: to.fullPath } });
  }

  // Login + permissão
  if (to.meta && to.meta.requiresAuth) {
    if (!uid) {
      return next({ name: "login", query: { return: to.fullPath } });
    }
    // Especial: Mezzomo (requiresSupreme)
    if (to.meta.requiresSupreme) {
      const currentMitarbeiterId = sessionStorage.getItem("mitarbeiterId");
      const supremeId = toDbFormat("M", "000000"); // 9000000
      if (currentMitarbeiterId === supremeId) return next();
      return next({ name: "startseite" });
    }

    const perms = modulesStore.permissions || {};
    if (perms[to.name]) return next();
    return next({ name: "startseite" });
  }

  // Fallback seguro
  return next();
});

// 🎯 Título dinâmico
// - Home: titleFixed = "Praxis Secondhand"
// - Login: titleFixed = "🔒 Login | System Praxis Secondhand"
// - Startseite: titleFixed = "System Praxis Secondhand"
// - Módulos: "<i18n(moduleI18nKey)> | System Praxis Secondhand"
// - Default: "System Praxis Secondhand"
function setTitle(to) {
  const brand = "System Praxis Secondhand";

  if (to?.meta?.titleFixed) {
    document.title = to.meta.titleFixed;
    return;
  }
  if (to?.meta?.vitrineTitleI18nKey) {
    document.title = tVitrineLocal(to.meta.vitrineTitleI18nKey);
    return;
  }
  if (to?.meta?.moduleI18nKey) {
    const mod = tLocal(to.meta.moduleI18nKey);
    document.title = `${mod} | ${brand}`;
    return;
  }
  document.title = brand;
}

/**
 * SEO dinâmico por rota.
 * - Home "/" => index,follow e canonical raiz.
 * - Demais rotas => noindex,nofollow e remove canonical.
 * - Query "?p=" na home mantém canonical raiz.
 */
function setSeoMeta(to) {
  const isHome = to.path === "/";
  upsertMeta("meta[name='robots']", {
    name: "robots",
    content: isHome ? "index,follow" : "noindex,nofollow",
  });

  const canonicalHref = "https://www.praxissecondhand.de/";
  const linkSel = "link[rel='canonical']";
  let link = document.querySelector(linkSel);
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
  }
  if (isHome) {
    link.setAttribute("href", canonicalHref);
    if (!link.parentNode) document.head.appendChild(link);
  } else if (link.parentNode) {
    document.head.removeChild(link);
  }
}

/** Cria/atualiza meta genérica. */
function upsertMeta(selector, attrs) {
  const el =
    document.head.querySelector(selector) || document.createElement("meta");
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  if (!el.parentNode) document.head.appendChild(el);
}

export default router;
